import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@/services/auth';
import { useAuth } from '@/hooks/useAuth';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(searchParams.get('error_description') || 'Authentication failed');
        return;
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      try {
        const success = await authService.exchangeCodeForTokens(code);
        if (success) {
          await refreshAuth();
          navigate('/', { replace: true });
        } else {
          setError('Failed to exchange authorization code');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Authentication error');
      }
    };

    handleCallback();
  }, [searchParams, navigate, refreshAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-error-600 dark:text-error-500 mb-2">
            Authentication Error
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <a
            href="/login"
            className="text-accent-600 hover:text-accent-700 font-medium"
          >
            Try again
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600"></div>
        <p className="text-gray-600 dark:text-gray-400">Completing sign in...</p>
      </div>
    </div>
  );
}
